import { GetServerSideProps } from 'next';
import getConfig from 'next/config';

import { HttpStatus } from '@kamernet/constants/HttpStatus';
import { parseLocale } from '@kamernet/core/Intl/utilities';
import { ROUTES, getRouteByPathname } from '@kamernet/core/Routing/routes';
import { loadTranslations } from '@kamernet/locales';
import { getDeviceType } from '@kamernet/utilities/UserAgent';

import { getApiClientPageLoader } from '../ApiClient/getApiClientPageLoader';
import { getAuthStatePageLoader } from '../Auth/getAuthStatePageLoader';
import { getEmptyAuthState } from '../Auth/utilities';
import { bootstrapServerApp } from '../Bootstrap/bootstrapServerApp';
import { initAppCookiesPageLoader } from '../Cookie/initAppCookiesPageLoader';
import { AppCookieName } from '../Cookie/types';
import { reportError } from '../Errors';
import { CustomTags, ErrorModules } from '../Errors/types';
import { getSessionFeatureFlagsPageLoader } from '../FeatureFlags/getSessionFeatureFlagsPageLoader';
import { getUserFeatureFlagsPageLoader } from '../FeatureFlags/getUserFeatureFlagsPageLoader';

import { PageLoaderErrorNotFound } from './PageLoaderErrorNotFound';
import { PageLoaderErrorRedirect } from './PageLoaderErrorRedirect';
import { TargetPageProps, PageProps, TargetPagePropsLoader } from './types';

const { publicRuntimeConfig } = getConfig();

export function definePageLoader<TTargetPageProps extends TargetPageProps>(
  targetPagePropsLoader: TargetPagePropsLoader<TTargetPageProps>,
): GetServerSideProps<PageProps> {
  return async ({ req, res, locale, query }) => {
    await bootstrapServerApp();
    const route = getRouteByPathname(req.pathname);

    if (!route) {
      throw new Error('route can not be empty');
    }

    const { isLandlordRoute, isPublic } = route.options;
    initAppCookiesPageLoader({ req, res });
    const anonymousID = req.cookies[AppCookieName.ANONYMOUS_ID] || '0';
    const deviceType = getDeviceType(req.headers['user-agent']);
    const parsedLocale = parseLocale(locale);
    const translations = await loadTranslations(parsedLocale);
    const apiClient = getApiClientPageLoader(req);
    let authState = getEmptyAuthState();

    const sessionFeatureFlags = await getSessionFeatureFlagsPageLoader(
      anonymousID,
    );

    try {
      authState = await getAuthStatePageLoader(
        req,
        res,
        sessionFeatureFlags,
        apiClient,
        parsedLocale,
        anonymousID,
      );

      const userFeatureFlags = await getUserFeatureFlagsPageLoader(
        anonymousID,
        authState.userProfile?.userid,
      );

      const featureFlags = {
        ...sessionFeatureFlags,
        ...userFeatureFlags,
      };

      const pageProps: PageProps = {
        type: 'PageProps',
        translations,
        authState,
        deviceType,
        featureFlags,
        anonymousID,
        traceID: req.traceID,
        targetPageProps: null,
        hasError404: false,
        hasError500: false,
        locale: parsedLocale,
      };

      if (!isPublic && !authState.userProfile) {
        const prevUrl =
          publicRuntimeConfig.ORIGIN +
          ROUTES.Home.getURL({}, parsedLocale, 'prefix');

        return {
          redirect: {
            permanent: false,
            destination: ROUTES.Signin.getURL(
              {
                ...(isLandlordRoute && {
                  isUserLandlord: 'true',
                }),
                prevUrl,
              },
              parsedLocale,
            ),
          },
        };
      }

      try {
        pageProps.targetPageProps = await targetPagePropsLoader({
          locale: parsedLocale,
          apiClient,
          query,
          res,
          userProfile: authState.userProfile,
        });

        return { props: pageProps };
      } catch (error) {
        if (error instanceof PageLoaderErrorRedirect) {
          return {
            redirect: {
              destination: error.destination,
              permanent: error.permenant,
            },
            props: {},
          };
        }

        if (error instanceof PageLoaderErrorNotFound) {
          pageProps.hasError404 = true;
          res.statusCode = HttpStatus.NOT_FOUND;

          return {
            props: pageProps,
          };
        }

        throw new Error('Failed to load target page props', { cause: error });
      }
    } catch (error) {
      res.statusCode = HttpStatus.INTERNAL_SERVER_ERROR;

      const userFeatureFlags = await getUserFeatureFlagsPageLoader(
        anonymousID,
        authState.userProfile?.userid,
      );

      const featureFlags = {
        ...sessionFeatureFlags,
        ...userFeatureFlags,
      };

      const pageProps: PageProps = {
        type: 'PageProps',
        translations,
        authState,
        deviceType,
        featureFlags,
        anonymousID,
        traceID: req.traceID,
        targetPageProps: null,
        hasError404: false,
        hasError500: true,
        locale: parsedLocale,
      };

      reportError(
        new Error('Unexpected error at loading page props', { cause: error }),
        {
          extra: {
            traceID: req.traceID,
          },
          tags: {
            [CustomTags.KN_Module]: ErrorModules.PageLoader,
          },
        },
      );

      return {
        props: pageProps,
      };
    }
  };
}
